// The user deficiency rules of the Safety Overview (Settings -> Filters). Twin of
// Backend/123bimlive/utils/safety_rules.py: the page sends the rule set with every
// request and the server applies it to the deficiencies, the crash correlation, the
// verdicts and the PDF. Kept in this browser.
//
// Defaults (2026-09-28): the existing definitions where they exist (5-station runs, every
// PSD Unsafe pass station), the new values only where nothing existed: GRW gap bridging
// and the MassDOT SOP TS-2021-001 passing-zone conditions. Stations are 10 ft apart.
//
// Part B: the other SOP conditions come from the road network (utils/road_network.py):
// our analyses first, then the state DOT, then OpenStreetMap — the order, the source of
// each condition and whether lower sources only fill gaps or add everything are the user's.
export const STATION_FT = 10;

// [key, label, the finding text ("passing zone near ...")] — twin of road_network.COND_*
export const NET_CONDITIONS = [
  ['signal', 'Traffic signal', 'a traffic signal'],
  ['majorIntersection', 'Intersection with a non-Local road', 'an intersection with a non-Local road'],
  ['busStop', 'Bus stop', 'a bus stop'],
  ['crosswalk', 'Marked crosswalk', 'a marked crosswalk'],
  ['school', 'School zone or school', 'a school'],
  ['emergency', 'Hospital, fire or EMS station', 'a hospital, fire or EMS station'],
  ['roundabout', 'Roundabout or circular intersection', 'a roundabout'],
  ['railCrossing', 'At-grade railroad crossing', 'a railroad crossing'],
  ['median', 'Raised median or two-way left-turn lane', 'a raised median or two-way left-turn lane'],
];
// the obstruction classes SSD / ISD record (twin of safety_rules.OBSTRUCTION_LABELS)
export const OBSTRUCTION_LABELS = {
  vegetation: 'Vegetation', terrain: 'Terrain', 'vertical curvature': 'Vertical curvature', 'guard rails': 'Guard rails',
  'concrete barrier': 'Concrete barrier', poles: 'Poles', signs: 'Signs', walls: 'Walls', fence: 'Fence', building: 'Building',
  vehicles: 'Vehicles', 'parked vehicle': 'Parked vehicle', 'street equipment': 'Street equipment', 'opposing left': 'Opposing left',
  other: 'Other', 'not relevant': 'Not relevant', unclassified: 'Unclassified',
};
export const OBSTRUCTION_ANALYSES = ['SSD', 'ISD'];

// existing guardrails ride along with the road network (no SOP condition): they shield a GRW warrant
export const GUARDRAIL_LABEL = 'Existing guardrail';
export const NET_SOURCES = [
  ['ours', 'Our analyses', 'traffic signs (road markings once that analysis writes results)'],
  ['dot', 'State DOT', 'the MassDOT inventory in Massachusetts'],
  ['osm', 'OpenStreetMap', 'every state'],
];
// what each source can tell, and where it is complete (twin of road_network.SIGN_* / DOT_ADAPTERS)
export const NET_SUPPLY = {
  ours: { supplies: ['signal', 'school', 'crosswalk', 'busStop', 'railCrossing', 'roundabout', 'emergency', 'median'], complete: ['railCrossing', 'school'] },
  dot: { supplies: NET_CONDITIONS.map(([c]) => c), complete: ['majorIntersection', 'crosswalk', 'school', 'roundabout', 'railCrossing'] },
  osm: { supplies: NET_CONDITIONS.map(([c]) => c), complete: [] },
};
const NET_MODES = ['fill', 'union'];
export const RULE_ANALYSES = ['SSD', 'PSD', 'GLR', 'FSA', 'GRW'];
export const STORAGE_KEY = 'safetyOverview:rules';

export const DEFAULT_RULES = {
  minStations: { SSD: 5, PSD: 1, GLR: 5, FSA: 5, GRW: 5 },
  grwGap: { on: true, noDataFt: 50, noWarrantFt: 30 },
  // an existing guardrail on the warrant's side of travel shields it; ignoreInventory shows our GRW results regardless
  grwShield: { ignoreInventory: false },
  // obstruction types to keep ([] = all): SSD stations / ISD maneuvers blocked by them stay; while
  // narrowed, the analyses without an obstruction (PSD, GLR, FSA, GRW, HCA) are hidden
  obstructions: [],
  psdSop: {
    intersection: { on: true, approachFt: 200, departFt: 100 },
    minZone: { on: true, ft: 800 },
    minSpeed: { on: true, mph: 45 },
  },
  intersectionSources: { isd: true, crashJunctions: true },
  network: {
    priority: ['ours', 'dot', 'osm'],
    mode: 'fill',
    conditions: Object.fromEntries(NET_CONDITIONS.map(([c]) => [c, { on: true, approachFt: 200, departFt: 100, source: 'auto' }])),
  },
};

const num = (v, d, lo = 0, hi = 1e6) => {
  const x = Number(v);
  return Number.isFinite(x) ? Math.min(Math.max(x, lo), hi) : d;
};
const bool = (v, d) => (typeof v === 'boolean' ? v : d);

/** A complete, sane rule set (defaults fill any gap). */
export function normalizeRules(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const d = DEFAULT_RULES;
  const ms = r.minStations || {};
  const g = r.grwGap || {};
  const s = r.psdSop || {};
  const si = s.intersection || {};
  const sz = s.minZone || {};
  const ss = s.minSpeed || {};
  const src = r.intersectionSources || {};
  return {
    minStations: Object.fromEntries(RULE_ANALYSES.map((a) => [a, Math.round(num(ms[a], d.minStations[a], 1, 1000))])),
    grwGap: { on: bool(g.on, d.grwGap.on), noDataFt: num(g.noDataFt, d.grwGap.noDataFt, 0, 5000), noWarrantFt: num(g.noWarrantFt, d.grwGap.noWarrantFt, 0, 5000) },
    grwShield: { ignoreInventory: bool((r.grwShield || {}).ignoreInventory, d.grwShield.ignoreInventory) },
    psdSop: {
      intersection: { on: bool(si.on, d.psdSop.intersection.on), approachFt: num(si.approachFt, d.psdSop.intersection.approachFt, 0, 5000), departFt: num(si.departFt, d.psdSop.intersection.departFt, 0, 5000) },
      minZone: { on: bool(sz.on, d.psdSop.minZone.on), ft: num(sz.ft, d.psdSop.minZone.ft, 0, 20000) },
      minSpeed: { on: bool(ss.on, d.psdSop.minSpeed.on), mph: num(ss.mph, d.psdSop.minSpeed.mph, 0, 100) },
    },
    intersectionSources: { isd: bool(src.isd, d.intersectionSources.isd), crashJunctions: bool(src.crashJunctions, d.intersectionSources.crashJunctions) },
    obstructions: Array.from(new Set((Array.isArray(r.obstructions) ? r.obstructions : [])
      .map((k) => String(k).trim().toLowerCase().slice(0, 40)).filter((k) => k && /^[a-z0-9 _-]+$/.test(k)))).sort(),
    network: normalizeNetwork(r.network),
  };
}

function normalizeNetwork(raw) {
  const n = raw && typeof raw === 'object' ? raw : {};
  const d = DEFAULT_RULES.network;
  const keys = NET_SOURCES.map(([k]) => k);
  const pr = Array.from(new Set((Array.isArray(n.priority) ? n.priority : []).filter((k) => keys.includes(k))));
  keys.forEach((k) => { if (!pr.includes(k)) pr.push(k); });
  const conds = n.conditions || {};
  return {
    priority: pr,
    mode: NET_MODES.includes(n.mode) ? n.mode : d.mode,
    conditions: Object.fromEntries(NET_CONDITIONS.map(([c]) => {
      const rc = conds[c] || {};
      const dc = d.conditions[c];
      return [c, { on: bool(rc.on, dc.on), approachFt: num(rc.approachFt, dc.approachFt, 0, 5000), departFt: num(rc.departFt, dc.departFt, 0, 5000),
        source: ['auto', ...keys].includes(rc.source) ? rc.source : dc.source }];
    })),
  };
}

export function loadRules() {
  try { return normalizeRules(JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')); } catch (e) { return normalizeRules(null); }
}
export function saveRules(rules) {
  const r = normalizeRules(rules);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(r)); } catch (e) { /* private window */ }
  return r;
}
export const rulesParam = (rules) => JSON.stringify(normalizeRules(rules));
export const isDefaultRules = (rules) => rulesParam(rules) === rulesParam(DEFAULT_RULES);

/** Short stable hash of a rule set (client-side cache keys). */
export function rulesHash(rules) {
  const s = rulesParam(rules);
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;   // eslint-disable-line no-bitwise
  return h.toString(36);
}
